import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Loader2, Plus, Search, Users2, Trash2, RefreshCw, X, ExternalLink, Box, ShoppingBag, GitMerge, AlertTriangle,
} from "lucide-react";
import {
  customersList, customersMeta, customerSave, customerDelete, customersBackfill, customersMerge,
  customerJobs, customerOrders, customerGet,
  CUSTOMER_TYPE_COLORS,
} from "../../../lib/customers";

const fmtUSD = (n) => Number(n || 0).toLocaleString("en-US", { style: "currency", currency: "USD" });
const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" }); } catch { return iso; }
};
const parseApiError = (e, fallback = "Save failed") => {
  const detail = e?.response?.data?.detail;
  if (!detail) return e?.message || fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((d) => d?.msg || JSON.stringify(d)).join("; ");
  return String(detail);
};

const TypePill = ({ type }) => {
  const c = CUSTOMER_TYPE_COLORS[type] || CUSTOMER_TYPE_COLORS.retail;
  return (
    <span className="px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.10em] rounded-sm whitespace-nowrap"
      style={{ background: c.bg, color: c.fg, border: `1px solid ${c.border}` }}>
      {c.label}
    </span>
  );
};

/* ========= Inline editable cell ========= */
const EditableCell = ({ value, type = "text", options = [], onCommit, format, placeholder = "—", numeric = false, align = "left" }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? "");
  const [busy, setBusy] = useState(false);

  const start = () => { setDraft(value ?? ""); setEditing(true); };
  const commit = async () => {
    let next = draft;
    if (type === "number") {
      next = (draft === "" || draft === null) ? null : (numeric ? parseInt(draft, 10) : parseFloat(draft));
      if (Number.isNaN(next)) next = null;
    } else if (type === "text") {
      next = (draft || "").trim() || null;
    }
    if (String(next ?? "") === String(value ?? "")) { setEditing(false); return; }
    setBusy(true);
    try { await onCommit(next); setEditing(false); } catch { setEditing(false); } finally { setBusy(false); }
  };
  const cancel = () => { setEditing(false); setDraft(value ?? ""); };
  if (editing) {
    if (type === "select") {
      return (
        <select autoFocus value={draft || ""} onChange={(e) => setDraft(e.target.value)}
          onBlur={commit} onKeyDown={(e) => { if (e.key === "Escape") cancel(); }}
          className="input-swiss w-full px-2 py-1 rounded-sm text-[12px]" disabled={busy}>
          <option value="">{placeholder}</option>
          {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      );
    }
    return (
      <input autoFocus type={type === "number" ? "number" : "text"} value={draft}
        onChange={(e) => setDraft(e.target.value)} onBlur={commit}
        onKeyDown={(e) => { if (e.key === "Escape") cancel(); if (e.key === "Enter") e.currentTarget.blur(); }}
        className="input-swiss w-full px-2 py-1 rounded-sm text-[12px]"
        style={{ textAlign: align }} disabled={busy} />
    );
  }
  const isEmpty = value == null || value === "";
  const display = isEmpty ? <em className="text-stone-400">{placeholder}</em> : (format ? format(value) : value);
  return (
    <button type="button" onClick={start}
      className="w-full px-1.5 py-1 rounded-sm hover:bg-[#FEF3C7] focus:bg-[#FEF3C7] outline-none transition truncate"
      title="Click to edit" style={{ textAlign: align, border: "1px dashed transparent", minHeight: 26 }}
      onMouseEnter={(e) => { e.currentTarget.style.border = "1px dashed var(--ups-border)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.border = "1px dashed transparent"; }}>
      {display}
    </button>
  );
};

/* ========= New customer form ========= */
const NewCustomerForm = ({ onSave, onCancel, busy }) => {
  const [form, setForm] = useState({ type: "retail", name: "", primary_email: "", phone: "", company: "", custom_discount_percent: "" });
  const u = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  return (
    <form onSubmit={(e) => {
      e.preventDefault();
      onSave({
        type: form.type, name: form.name.trim(),
        primary_email: form.primary_email.trim() || null,
        phone: form.phone.trim() || null,
        company: form.company.trim() || null,
        custom_discount_percent: form.custom_discount_percent === "" ? null : parseInt(form.custom_discount_percent, 10),
      });
    }} className="p-4 rounded-sm grid sm:grid-cols-3 gap-3" style={{ background: "var(--ups-muted)", border: "1px dashed var(--ups-ink)" }} data-testid="new-customer-form">
      <label className="block">
        <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-[color:var(--ups-subtext)]">Type</span>
        <select value={form.type} onChange={(e) => u("type", e.target.value)} className="input-swiss w-full px-3 py-2 mt-1 rounded-sm text-sm" data-testid="new-customer-type">
          <option value="retail">Retail (walk-in)</option>
          <option value="ups_store">UPS Store</option>
          <option value="house_account">House Account</option>
        </select>
      </label>
      <label className="block sm:col-span-2">
        <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-[color:var(--ups-subtext)]">Name {form.type === "retail" ? "(person or business)" : "(store name / company)"}</span>
        <input required value={form.name} onChange={(e) => u("name", e.target.value)} className="input-swiss w-full px-3 py-2 mt-1 rounded-sm text-sm" data-testid="new-customer-name" />
      </label>
      <label className="block">
        <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-[color:var(--ups-subtext)]">Email {form.type !== "retail" && <span className="text-[#DC2626]">*</span>}</span>
        <input type="email" required={form.type !== "retail"} value={form.primary_email} onChange={(e) => u("primary_email", e.target.value)} className="input-swiss w-full px-3 py-2 mt-1 rounded-sm text-sm" data-testid="new-customer-email" />
      </label>
      <label className="block">
        <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-[color:var(--ups-subtext)]">Phone</span>
        <input value={form.phone} onChange={(e) => u("phone", e.target.value)} className="input-swiss w-full px-3 py-2 mt-1 rounded-sm text-sm" placeholder="(optional)" />
      </label>
      <label className="block">
        <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-[color:var(--ups-subtext)]">Custom discount %</span>
        <input type="number" min="0" max="90" value={form.custom_discount_percent} onChange={(e) => u("custom_discount_percent", e.target.value)} className="input-swiss w-full px-3 py-2 mt-1 rounded-sm text-sm" placeholder="—" disabled={form.type === "retail"} />
      </label>
      <label className="block sm:col-span-2">
        <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-[color:var(--ups-subtext)]">Company</span>
        <input value={form.company} onChange={(e) => u("company", e.target.value)} className="input-swiss w-full px-3 py-2 mt-1 rounded-sm text-sm" />
      </label>
      <div className="sm:col-span-3 flex justify-end gap-2">
        <button type="button" onClick={onCancel} className="px-3 py-2 text-[12px] rounded-sm hover:bg-white" style={{ border: "1px solid var(--ups-border)" }}>
          <X size={12} className="inline mr-1" />Cancel
        </button>
        <button type="submit" disabled={busy} className="btn-brutal px-3 py-2 text-[12px] rounded-sm font-bold uppercase tracking-[0.14em]" data-testid="new-customer-save">
          {busy ? <Loader2 size={12} className="animate-spin" /> : "Add Customer"}
        </button>
      </div>
    </form>
  );
};

/* ========= Customer detail drawer ========= */
const CustomerDrawer = ({ customerId, onClose, onChanged }) => {
  const [data, setData] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [orders, setOrders] = useState([]);
  const [tab, setTab] = useState("jobs");
  useEffect(() => {
    if (!customerId) return;
    Promise.all([customerGet(customerId), customerJobs(customerId), customerOrders(customerId)])
      .then(([c, j, o]) => { setData(c); setJobs(j); setOrders(o); })
      .catch((e) => toast.error(parseApiError(e, "Load failed")));
  }, [customerId]);
  if (!customerId) return null;
  const c = data;
  return (
    <div className="fixed inset-0 z-40" data-testid="customer-drawer">
      <button className="absolute inset-0 bg-black/40" onClick={onClose} aria-label="Close" />
      <div className="absolute right-0 top-0 bottom-0 w-full max-w-[640px] bg-white overflow-y-auto" style={{ borderLeft: "2px solid var(--ups-ink)" }}>
        <div className="sticky top-0 z-10 flex items-start justify-between p-4" style={{ background: "var(--ups-ink)", color: "#FFB500", borderBottom: "1px solid #FFB500" }}>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.18em] opacity-70">Customer</div>
            <h3 className="font-display text-xl font-black mt-0.5">{c?.name || "Loading…"}</h3>
            {c && <div className="mt-1.5"><TypePill type={c.type} /></div>}
          </div>
          <button onClick={onClose} className="p-1.5 rounded-sm hover:bg-white/10" aria-label="Close"><X size={16} /></button>
        </div>
        {!c ? <div className="p-10 text-center text-stone-500"><Loader2 size={16} className="inline animate-spin mr-2" />Loading…</div> : (
          <div className="p-4 space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <div className="card-swiss p-3 rounded-sm">
                <div className="overline">Lifetime $</div>
                <div className="font-display text-xl font-black mt-0.5" style={{ color: "var(--ups-accent)" }}>{fmtUSD(c.lifetime_revenue)}</div>
              </div>
              <div className="card-swiss p-3 rounded-sm">
                <div className="overline">Orders</div>
                <div className="font-display text-xl font-black mt-0.5">{c.total_orders}</div>
              </div>
              <div className="card-swiss p-3 rounded-sm">
                <div className="overline">Jobs</div>
                <div className="font-display text-xl font-black mt-0.5">{c.total_jobs}</div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 text-[12px]">
              <div><div className="overline mb-1">Email</div><div className="font-mono">{c.primary_email || "—"}</div></div>
              <div><div className="overline mb-1">Phone</div><div className="font-mono">{c.phone || "—"}</div></div>
              <div><div className="overline mb-1">Domain</div><div className="font-mono">{c.domain || "—"}</div></div>
              <div><div className="overline mb-1">Custom discount</div><div className="font-mono font-bold" style={{ color: c.custom_discount_percent != null ? "var(--ups-accent)" : "var(--ups-subtext)" }}>{c.custom_discount_percent != null ? `${c.custom_discount_percent}%` : "uses domain default"}</div></div>
              <div className="col-span-2"><div className="overline mb-1">Last seen</div><div className="font-mono text-[11px]">{fmtDate(c.last_seen_at)}</div></div>
            </div>
            <div className="flex border-b" style={{ borderColor: "var(--ups-border)" }}>
              {[
                { id: "jobs", label: `Jobs (${jobs.length})`, icon: Box },
                { id: "orders", label: `Orders (${orders.length})`, icon: ShoppingBag },
              ].map((t) => (
                <button key={t.id} onClick={() => setTab(t.id)} className={`px-3 py-2 text-[11px] font-bold uppercase tracking-[0.14em] flex items-center gap-1.5 border-b-2 ${tab === t.id ? "border-[color:var(--ups-ink)]" : "border-transparent"}`} style={{ color: tab === t.id ? "var(--ups-ink)" : "var(--ups-subtext)" }}>
                  <t.icon size={12} /> {t.label}
                </button>
              ))}
            </div>
            {tab === "jobs" && (
              jobs.length === 0 ? <div className="text-stone-500 text-sm p-4">No jobs yet.</div> :
              <div className="space-y-1.5">
                {jobs.map((j) => (
                  <div key={j.id} className="p-2 rounded-sm flex items-center justify-between gap-2 text-[12px]" style={{ border: "1px solid var(--ups-border)" }}>
                    <div className="min-w-0">
                      <div className="font-mono text-[10px] text-stone-500">{j.job_number} · {j.status}</div>
                      <div className="font-medium truncate">{j.name}</div>
                    </div>
                    <div className="font-mono font-bold">{fmtUSD(j.total_cost)}</div>
                  </div>
                ))}
              </div>
            )}
            {tab === "orders" && (
              orders.length === 0 ? <div className="text-stone-500 text-sm p-4">No orders yet.</div> :
              <div className="space-y-1.5">
                {orders.map((o) => (
                  <div key={o.id} className="p-2 rounded-sm flex items-center justify-between gap-2 text-[12px]" style={{ border: "1px solid var(--ups-border)" }}>
                    <div className="min-w-0">
                      <div className="font-mono text-[10px] text-stone-500">{o.order_number} · {fmtDate(o.created_at)}</div>
                      <div className="font-medium truncate">{(o.product_description || "").slice(0, 90) || o.product_name || "—"}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-mono font-bold" style={{ color: "var(--ups-accent)" }}>{fmtUSD(o.ups_price || o.retail_price || 0)}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

/* ========= Main tab ========= */
const CustomersTab = () => {
  const [list, setList] = useState([]);
  const [meta, setMeta] = useState({ types: ["ups_store", "house_account", "retail"], counts: {} });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ type: "", q: "", archived: false });
  const [viewMode, setViewMode] = useState("active"); // 'active' | 'all'
  const [sort, setSort] = useState({ key: "updated_at", dir: "desc" }); // 'updated_at' | 'lifetime' | 'orders' | 'name'
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedForMerge, setSelectedForMerge] = useState(new Set());
  const [mergeModal, setMergeModal] = useState(null); // { customers: [...] } when open

  const reload = async () => {
    setLoading(true);
    try {
      const [items, m] = await Promise.all([
        customersList({
          type: filters.type || undefined,
          archived: filters.archived,
          q: filters.q || undefined,
          with_active_jobs: viewMode === "active" ? true : undefined,
        }),
        customersMeta(),
      ]);
      setList(items);
      setMeta(m);
    } catch (e) {
      toast.error(parseApiError(e, "Load failed"));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [filters.type, filters.q, filters.archived, viewMode]);

  // Client-side sort (small lists, fine to do here; saves a backend round-trip)
  const sortedList = useMemo(() => {
    const arr = [...list];
    const dir = sort.dir === "asc" ? 1 : -1;
    const key = sort.key;
    arr.sort((a, b) => {
      let va, vb;
      if (key === "lifetime") { va = a.lifetime_revenue || 0; vb = b.lifetime_revenue || 0; }
      else if (key === "orders") { va = a.total_orders || 0; vb = b.total_orders || 0; }
      else if (key === "name") { va = (a.name || "").toLowerCase(); vb = (b.name || "").toLowerCase(); }
      else { va = a.updated_at || ""; vb = b.updated_at || ""; }
      if (va === vb) return 0;
      return va < vb ? -dir : dir;
    });
    return arr;
  }, [list, sort]);

  const toggleSort = (key) => {
    setSort((p) => p.key === key ? { key, dir: p.dir === "asc" ? "desc" : "asc" } : { key, dir: "desc" });
  };

  const handleCreate = async (payload) => {
    setBusy(true);
    try {
      await customerSave(null, payload);
      toast.success("Customer added");
      setCreating(false);
      await reload();
    } catch (e) { toast.error(parseApiError(e, "Save failed")); }
    finally { setBusy(false); }
  };

  const handlePatchField = async (c, field, value) => {
    try {
      const updated = await customerSave(c.id, { [field]: value });
      setList((prev) => prev.map((x) => x.id === c.id ? { ...x, ...updated } : x));
    } catch (e) { toast.error(parseApiError(e, "Save failed")); throw e; }
  };

  const handleDelete = async (c) => {
    if (!window.confirm(`Delete ${c.name}? Linked jobs/orders will be detached but kept.`)) return;
    try { await customerDelete(c.id); toast.success("Deleted"); await reload(); }
    catch (e) { toast.error(parseApiError(e, "Delete failed")); }
  };

  const handleBackfill = async () => {
    if (!window.confirm("Scan existing orders + sessions and create Customer records for any unique email? Idempotent — won't duplicate.")) return;
    try {
      const r = await customersBackfill();
      toast.success(`Backfilled — ${r.inserted} new, ${r.linked_orders} orders + ${r.linked_jobs} jobs linked.`);
      await reload();
    } catch (e) { toast.error(parseApiError(e, "Backfill failed")); }
  };

  const toggleMergeSelect = (id) => {
    setSelectedForMerge((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const clearMergeSelection = () => setSelectedForMerge(new Set());

  const openMergeModal = () => {
    const picked = sortedList.filter((c) => selectedForMerge.has(c.id));
    if (picked.length < 2) {
      toast.error("Select at least 2 customers to merge.");
      return;
    }
    setMergeModal({ customers: picked });
  };

  const handleMerge = async (keeperId) => {
    const sourceIds = [...selectedForMerge].filter((id) => id !== keeperId);
    setBusy(true);
    try {
      const r = await customersMerge(keeperId, sourceIds);
      toast.success(
        `Merged ${r.deleted_count} into keeper — ` +
        `${r.jobs_relinked} job(s) + ${r.orders_relinked} order(s) re-linked.`
      );
      setMergeModal(null);
      clearMergeSelection();
      await reload();
    } catch (e) { toast.error(parseApiError(e, "Merge failed")); }
    finally { setBusy(false); }
  };

  const filterPills = [
    { id: "", label: "All", count: meta.counts?.total ?? list.length },
    { id: "ups_store", label: "UPS Stores", count: meta.counts?.ups_store ?? 0 },
    { id: "house_account", label: "House Accounts", count: meta.counts?.house_account ?? 0 },
    { id: "retail", label: "Retail", count: meta.counts?.retail ?? 0 },
  ];

  if (loading && list.length === 0) {
    return <div className="p-10 text-[color:var(--ups-subtext)] flex items-center"><Loader2 className="animate-spin mr-2" size={16} />Loading customers…</div>;
  }

  return (
    <div className="space-y-4" data-testid="customers-tab">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-display text-2xl font-bold flex items-center gap-2"><Users2 size={20} /> Customers</h3>
          <p className="text-sm text-[color:var(--ups-subtext)] mt-1">
            UPS stores &amp; house accounts auto-create on their first <span className="font-mono">/api/orders</span>. Retail walk-ins are added manually here.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={handleBackfill} className="px-3 py-2 text-[11px] rounded-sm font-bold uppercase tracking-[0.14em] flex items-center gap-1.5 hover:bg-[color:var(--ups-muted)]" style={{ border: "1px solid var(--ups-border)" }} data-testid="backfill-btn">
            <RefreshCw size={12} /> Backfill from Orders
          </button>
          {!creating && (
            <button onClick={() => setCreating(true)} className="btn-brutal px-3 py-2 text-[11px] rounded-sm font-bold uppercase tracking-[0.14em] flex items-center gap-1.5" data-testid="new-customer-btn">
              <Plus size={12} /> New Customer
            </button>
          )}
        </div>
      </div>

      {/* Filter pills */}
      <div className="flex flex-wrap gap-1.5" data-testid="customer-filter-pills">
        {filterPills.map((p) => {
          const active = filters.type === p.id;
          return (
            <button key={p.id || "all"} onClick={() => setFilters((f) => ({ ...f, type: p.id }))}
              className="px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.14em] rounded-sm flex items-center gap-2"
              style={{
                background: active ? "var(--ups-ink)" : "#fff",
                color: active ? "#FFB500" : "var(--ups-ink)",
                border: `1px solid ${active ? "var(--ups-ink)" : "var(--ups-border)"}`,
              }}
              data-testid={`pill-${p.id || "all"}`}
            >
              {p.label}
              <span className="font-mono opacity-70">{p.count}</span>
            </button>
          );
        })}
      </div>

      {creating && <NewCustomerForm onSave={handleCreate} onCancel={() => setCreating(false)} busy={busy} />}

      {/* View tabs: Active Orders vs All Customers */}
      <div className="flex items-center gap-0 rounded-sm overflow-hidden" style={{ border: "1px solid var(--ups-ink)", width: "fit-content" }}>
        {[
          { id: "active", label: "Active Orders" },
          { id: "all", label: "All Customers" },
        ].map((v) => {
          const active = viewMode === v.id;
          return (
            <button key={v.id} type="button" onClick={() => setViewMode(v.id)}
              className="px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.14em]"
              style={{
                background: active ? "var(--ups-ink)" : "transparent",
                color: active ? "#FFB500" : "var(--ups-ink)",
              }}
              data-testid={`customers-view-${v.id}`}>
              {v.label}
              {active && v.id === "active" && (
                <span style={{ marginLeft: 8, opacity: 0.9 }}>· {sortedList.length}</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Search + archive toggle */}
      <div className="flex items-center gap-2 flex-wrap p-3 rounded-sm" style={{ background: "var(--ups-muted)", border: "1px solid var(--ups-border)" }}>
        <div className="relative flex-1 min-w-[220px]">
          <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-stone-500" />
          <input type="search" placeholder="Search name, email, phone, domain…" value={filters.q}
            onChange={(e) => setFilters((p) => ({ ...p, q: e.target.value }))}
            className="input-swiss w-full pl-7 pr-3 py-1.5 rounded-sm text-[12px]" data-testid="customer-search" />
        </div>
        <label className="flex items-center gap-1 text-[11px] font-bold uppercase tracking-[0.12em]">
          <input type="checkbox" checked={filters.archived} onChange={(e) => setFilters((p) => ({ ...p, archived: e.target.checked }))} /> Archived
        </label>
      </div>

      {/* Merge action bar — appears when ≥1 row checked */}
      {selectedForMerge.size > 0 && (
        <div className="flex items-center gap-3 px-3 py-2 rounded-sm text-[12px]"
          style={{ background: "#FEF3C7", border: "1px solid #D97706" }} data-testid="merge-action-bar">
          <strong>{selectedForMerge.size} customer{selectedForMerge.size === 1 ? "" : "s"} selected</strong>
          <span className="text-stone-700">Pick 2+ to merge their jobs/orders into one record.</span>
          <div className="ml-auto flex gap-2">
            <button type="button" onClick={clearMergeSelection}
              className="px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.14em] rounded-sm hover:bg-white"
              style={{ border: "1px solid var(--ups-border)" }} data-testid="merge-clear">
              Clear
            </button>
            <button type="button" onClick={openMergeModal}
              disabled={selectedForMerge.size < 2}
              className="btn-brutal px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.14em] flex items-center gap-1.5 disabled:opacity-40"
              data-testid="merge-open-modal-btn">
              <GitMerge size={12} /> Merge {selectedForMerge.size}
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      {list.length === 0 ? (
        <div className="p-10 text-center text-stone-500 rounded-sm" style={{ border: "1px dashed var(--ups-border)", background: "#fff" }}>
          {filters.archived ? "No archived customers." : "No customers match these filters."}
        </div>
      ) : (
        <div className="rounded-sm overflow-hidden" style={{ border: "1px solid var(--ups-border)", background: "#fff" }}>
          <table className="w-full text-[12px]">
            <thead style={{ background: "var(--ups-muted)" }}>
              <tr className="text-left">
                <th className="px-2 py-2 w-[32px]">
                  <input type="checkbox"
                    aria-label="Select all on page"
                    checked={sortedList.length > 0 && sortedList.every((c) => selectedForMerge.has(c.id))}
                    onChange={(e) => {
                      if (e.target.checked) setSelectedForMerge(new Set(sortedList.map((c) => c.id)));
                      else clearMergeSelection();
                    }}
                    data-testid="merge-select-all"
                  />
                </th>
                {[
                  { label: "Type", sortKey: null, align: "left" },
                  { label: "Name", sortKey: "name", align: "left" },
                  { label: "Email", sortKey: null, align: "left" },
                  { label: "Phone", sortKey: null, align: "left" },
                  { label: "Discount %", sortKey: null, align: "right" },
                  { label: "Lifetime $", sortKey: "lifetime", align: "right" },
                  { label: "Orders", sortKey: "orders", align: "right" },
                  { label: "Jobs", sortKey: null, align: "right" },
                  { label: "Last Seen", sortKey: "updated_at", align: "left" },
                  { label: "", sortKey: null, align: "left" },
                ].map((h, i) => {
                  const sortable = !!h.sortKey;
                  const active = sort.key === h.sortKey;
                  return (
                    <th key={`${h.label}-${i}`}
                      onClick={sortable ? () => toggleSort(h.sortKey) : undefined}
                      data-testid={sortable ? `customer-col-sort-${h.sortKey}` : undefined}
                      className={`px-3 py-2 text-[10px] font-bold uppercase tracking-[0.14em] select-none ${sortable ? "cursor-pointer hover:underline" : ""}`}
                      style={{ textAlign: h.align }}>
                      {h.label}
                      {sortable && active ? (sort.dir === "asc" ? " ▲" : " ▼") : ""}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {sortedList.map((c) => (
                <tr key={c.id} className="border-t hover:bg-[color:var(--ups-muted)]" style={{ borderColor: "var(--ups-border)" }} data-testid={`customer-row-${c.id}`}>
                  <td className="px-2 py-2 text-center">
                    <input type="checkbox"
                      checked={selectedForMerge.has(c.id)}
                      onChange={() => toggleMergeSelect(c.id)}
                      aria-label={`Select ${c.name} for merge`}
                      data-testid={`merge-select-${c.id}`}
                    />
                  </td>
                  <td className="px-2 py-1" data-testid={`customer-type-cell-${c.id}`}>
                    <EditableCell
                      value={c.type}
                      type="select"
                      options={[
                        { value: "ups_store", label: "UPS Store" },
                        { value: "house_account", label: "House Account" },
                        { value: "retail", label: "Retail" },
                      ]}
                      onCommit={(v) => handlePatchField(c, "type", v)}
                      format={(v) => <TypePill type={v} />}
                    />
                  </td>
                  <td className="px-2 py-1 font-medium">
                    <EditableCell value={c.name} onCommit={(v) => handlePatchField(c, "name", v || c.name)} />
                  </td>
                  <td className="px-2 py-1 font-mono text-[11px] max-w-[220px] truncate">
                    <EditableCell value={c.primary_email} onCommit={(v) => handlePatchField(c, "primary_email", v)} />
                  </td>
                  <td className="px-2 py-1 font-mono text-[11px] max-w-[140px]">
                    <EditableCell value={c.phone} onCommit={(v) => handlePatchField(c, "phone", v)} />
                  </td>
                  <td className="px-2 py-1 font-mono text-right">
                    <EditableCell
                      value={c.custom_discount_percent}
                      type="number" numeric align="right"
                      format={(v) => `${v}%`}
                      onCommit={(v) => handlePatchField(c, "custom_discount_percent", v)}
                      placeholder="default"
                    />
                  </td>
                  <td className="px-3 py-2 font-mono font-bold text-right" style={{ color: "var(--ups-accent)" }}>{fmtUSD(c.lifetime_revenue)}</td>
                  <td className="px-3 py-2 font-mono text-right">{c.total_orders}</td>
                  <td className="px-3 py-2 font-mono text-right">{c.total_jobs}</td>
                  <td className="px-3 py-2 font-mono text-[10px] text-stone-500 whitespace-nowrap">{fmtDate(c.last_seen_at)}</td>
                  <td className="px-2 py-2 text-right whitespace-nowrap">
                    <button onClick={() => setSelectedId(c.id)} className="p-1.5 rounded-sm hover:bg-white" aria-label="Open" data-testid={`open-customer-${c.id}`}><ExternalLink size={13} /></button>
                    <button onClick={() => handleDelete(c)} className="p-1.5 rounded-sm hover:bg-[#FEE2E2] ml-1" aria-label="Delete"><Trash2 size={13} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selectedId && <CustomerDrawer customerId={selectedId} onClose={() => setSelectedId(null)} onChanged={reload} />}
      {mergeModal && (
        <MergeModal
          customers={mergeModal.customers}
          busy={busy}
          onCancel={() => setMergeModal(null)}
          onConfirm={handleMerge}
        />
      )}
    </div>
  );
};

const MergeModal = ({ customers, busy, onCancel, onConfirm }) => {
  const [keeperId, setKeeperId] = useState(customers[0]?.id || "");
  const sources = customers.filter((c) => c.id !== keeperId);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="merge-modal">
      <button className="absolute inset-0 bg-black/50" onClick={onCancel} aria-label="Close" />
      <div className="relative w-full max-w-[700px] rounded-sm overflow-hidden" style={{ background: "#fff", border: "2px solid var(--ups-ink)" }}>
        <div className="flex items-center justify-between px-4 py-3" style={{ background: "var(--ups-ink)", color: "#FFB500" }}>
          <h3 className="font-display text-[18px] font-bold flex items-center gap-2"><GitMerge size={18} /> Merge Customers</h3>
          <button onClick={onCancel} className="p-1.5 rounded-sm hover:bg-white/10" aria-label="Close"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-4 text-[12px]">
          <div className="flex items-start gap-2 p-3 rounded-sm" style={{ background: "#FEF3C7", border: "1px solid #D97706" }}>
            <AlertTriangle size={16} className="shrink-0 mt-0.5" />
            <div>
              <strong>Hard delete</strong> — the {sources.length} non-keeper record{sources.length === 1 ? "" : "s"} will be permanently deleted. All linked jobs &amp; orders will be re-pointed to the keeper. This cannot be undone.
            </div>
          </div>

          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.14em] mb-2 text-[color:var(--ups-subtext)]">
              Pick the "keeper" record (the one to merge everything into):
            </div>
            <div className="space-y-2" data-testid="keeper-picker">
              {customers.map((c) => {
                const sel = c.id === keeperId;
                return (
                  <label key={c.id}
                    className="flex items-start gap-3 p-3 rounded-sm cursor-pointer"
                    style={{
                      background: sel ? "#FFB500" : "#fff",
                      border: `2px solid ${sel ? "var(--ups-ink)" : "var(--ups-border)"}`,
                    }}
                    data-testid={`keeper-option-${c.id}`}
                  >
                    <input type="radio" name="keeper" checked={sel} onChange={() => setKeeperId(c.id)} className="mt-1" />
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-[13px]">{c.name}</div>
                      <div className="text-[11px] font-mono opacity-70 truncate">{c.primary_email}</div>
                      <div className="text-[10px] mt-1 flex flex-wrap gap-2">
                        <span>type: <strong>{c.type}</strong></span>
                        <span>· orders: <strong>{c.total_orders || 0}</strong></span>
                        <span>· jobs: <strong>{c.total_jobs || 0}</strong></span>
                        <span>· lifetime: <strong>{fmtUSD(c.lifetime_revenue)}</strong></span>
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t" style={{ borderColor: "var(--ups-border)" }}>
            <button type="button" onClick={onCancel}
              className="px-4 py-2 text-[12px] font-bold uppercase tracking-[0.14em] rounded-sm hover:bg-[color:var(--ups-muted)]"
              style={{ border: "1px solid var(--ups-border)" }} data-testid="merge-cancel">
              Cancel
            </button>
            <button type="button" onClick={() => onConfirm(keeperId)} disabled={busy || !keeperId}
              className="btn-brutal px-4 py-2 text-[12px] font-bold uppercase tracking-[0.14em] flex items-center gap-1.5 disabled:opacity-40"
              data-testid="merge-confirm">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <GitMerge size={14} />}
              Merge {sources.length} into keeper
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomersTab;
